"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Tag } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto max-w-4xl px-4">
          <div className="space-y-8">
            <div className="h-12 w-3/4 rounded-lg bg-muted/30 animate-pulse" />
            <div className="h-96 w-full rounded-xl bg-muted/30 animate-pulse" />
            <div className="space-y-4">
              <div className="h-4 w-full rounded bg-muted/30 animate-pulse" />
              <div className="h-4 w-5/6 rounded bg-muted/30 animate-pulse" />
              <div className="h-4 w-4/6 rounded bg-muted/30 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto max-w-4xl px-4">
          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="py-8">
              <p className="text-center text-destructive">Failed to load post. Please try again later.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto max-w-4xl px-4">
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-foreground">Post not found.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-background">
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <Link
          to="/BlogPage"
          className="inline-flex items-center gap-2 text-sm text-foreground hover:text-foreground transition-colors duration-[var(--duration-medium)] mb-8"
        >
          <ArrowLeft className="size-4" />
          Back to Blog
        </Link>

        <header className="mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground">
            {post.date && (
              <div className="flex items-center gap-2">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </time>
              </div>
            )}

            {post.author && (
              <div className="flex items-center gap-2">
                <User className="size-4" />
                <span>{post.author.name}</span>
              </div>
            )}
          </div>

          {(post.categories && post.categories.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Tag className="size-4 text-foreground/70" />
              {post.categories.map((category, i) => (
                <span
                  key={category.id}
                  data-index={i}
                  className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-primary-light text-foreground"
                >
                  {category.name}
                </span>
              ))}
            </div>
          )}
        </header>

        {post.featuredImage && (
          <div className="mb-10 rounded-xl overflow-hidden shadow-medium">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-auto object-cover"
              fetchpriority="high"
            />
          </div>
        )}

        {post.content && (
          <div
            className="prose prose-lg max-w-none mb-12 text-foreground"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}

        <Separator className="my-12" />

        {post.author && (
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="text-foreground">About the Author</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                <Avatar className="size-16 shrink-0">
                  {post.author.avatar?.["96"] && (
                    <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                  )}
                  <AvatarFallback className="bg-primary text-foreground-primary text-lg">
                    {post.author.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg text-foreground mb-1">
                    {post.author.name}
                  </h3>
                  {post.author.link && (
                    <Link
                      to={post.author.link}
                      className="text-sm text-foreground hover:text-foreground transition-colors duration-[var(--duration-fast)]"
                    >
                      View all posts →
                    </Link>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <RelatedPostsSection currentPostId={post.id} categories={post.categories || []} />
      </div>
    </article>
  );
}

function RelatedPostsSection({ currentPostId, categories }: { currentPostId: string; categories: Array<{ id: number }> }) {
  const categoryIds = categories.map(cat => cat.id);

  if (categoryIds.length === 0) {
    return null;
  }

  return (
    <section>
      <h2 className="text-2xl font-display font-bold text-foreground mb-6">Related Posts</h2>
      <PostsProvider wp_query={{
          per_page: 3,
          exclude: [currentPostId],
          categories: categoryIds,
          orderby: "date",
          order: "desc"
        }}>
        <RelatedPostsGrid />
      </PostsProvider>
    </section>
  );
}

function RelatedPostsGrid() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 rounded-xl bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="hover-lift group">
          {post.featuredImage && (
            <div className="overflow-hidden rounded-t-xl">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover transition-transform duration-[var(--duration-slow)] group-hover:scale-105"
                fetchpriority="high"
              />
            </div>
          )}
          <CardContent className="space-y-3">
            <h3 className="font-semibold text-lg text-foreground leading-tight line-clamp-2">
              {post.title}
            </h3>
            {post.excerpt && (
              <div
                className="text-sm text-foreground line-clamp-3"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            )}
            {post.link && (
              <Link to={post.link}>
                <Button variant="outline" size="sm" className="w-full">
                  Read More
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();
  const currentPostType = wvcClient.getCurrentPostType();

  return (
    <PostsProvider postType={currentPostType} wp_query={{ include: [currentPostId], per_page: 1 }}>
      <SinglePostContent />
    </PostsProvider>
  );
}